/**
 * Client-side logger following ADR-028 patterns.
 *
 * Provides a pino-like API:
 *   logger.debug(), logger.info(), logger.warn(), logger.error()
 *
 * In production mode (import.meta.env.PROD) debug and info levels are silenced.
 * In development mode all levels are formatted to the console with a level prefix.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: unknown;
}

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

const LOG_LEVEL_STYLE: Record<LogLevel, string> = {
  debug: 'color: #888; font-weight: normal',
  info: 'color: #0066cc; font-weight: normal',
  warn: 'color: #cc8800; font-weight: bold',
  error: 'color: #cc0000; font-weight: bold',
};

/** The minimum level that will be output. */
const MIN_LEVEL: LogLevel = import.meta.env.PROD ? 'warn' : 'debug';

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[MIN_LEVEL];
}

function formatMessage(level: LogLevel, message: string, context?: LogContext): void {
  if (!shouldLog(level)) return;

  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] ${level.toUpperCase()}`;

  const consoleFn =
    level === 'error'
      ? console.error
      : level === 'warn'
        ? console.warn
        : level === 'debug'
          ? console.debug
          : console.info;

  if (context && Object.keys(context).length > 0) {
    consoleFn(`%c${prefix}%c ${message}`, LOG_LEVEL_STYLE[level], '', context);
  } else {
    consoleFn(`%c${prefix}%c ${message}`, LOG_LEVEL_STYLE[level], '');
  }
}

/**
 * Create a child logger with a fixed context that is merged into every log call.
 */
function createLogger(baseContext?: LogContext) {
  return {
    debug(message: string, context?: LogContext): void {
      formatMessage('debug', message, { ...baseContext, ...context });
    },

    info(message: string, context?: LogContext): void {
      formatMessage('info', message, { ...baseContext, ...context });
    },

    warn(message: string, context?: LogContext): void {
      formatMessage('warn', message, { ...baseContext, ...context });
    },

    error(message: string, context?: LogContext): void {
      formatMessage('error', message, { ...baseContext, ...context });
    },

    /** Create a child logger that inherits this logger's base context. */
    child(childContext: LogContext) {
      return createLogger({ ...baseContext, ...childContext });
    },
  };
}

/** Root application logger. */
export const logger = createLogger({ module: 'app' });

export type Logger = ReturnType<typeof createLogger>;
